import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import { MarketingAnalyticsService } from 'src/app/private/services/marketing-analytics.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

Chart.register(...registerables);

interface Metric {
  value: number | null;
  changePct: number | null;
  changeLabel: string | null;
}

@Component({
  selector: 'app-marketing-analytics-report',
  templateUrl: './marketing-analytics-report.component.html',
  styleUrls: ['./marketing-analytics-report.component.scss'],
})
export class MarketingAnalyticsReportComponent implements OnInit, OnDestroy {
  @ViewChild('channelChart') channelCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('trendChart') trendCanvas?: ElementRef<HTMLCanvasElement>;

  loading = true;
  error = '';
  rangeDays = '30';
  fromDate = '';
  toDate = '';
  showAllCampaigns = false;
  report: any = this.emptyReport();
  audienceSlices = this.buildAudienceSlices();
  donutGradient = 'conic-gradient(#e2e8f0 0% 100%)';

  private channelChart?: Chart;
  private trendChart?: Chart;
  private chartTimer?: ReturnType<typeof setTimeout>;
  private appliedRange = '';
  private requestId = 0;

  constructor(
    private api: MarketingAnalyticsService,
    private localStorage: LocalStorageService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.syncAudience();
    this.applyRange(this.rangeDays);
  }

  ngOnDestroy(): void {
    clearTimeout(this.chartTimer);
    this.channelChart?.destroy();
    this.trendChart?.destroy();
  }

  onRangeChange(event: Event): void {
    const next = (event.target as HTMLSelectElement).value;
    if (!next || next === this.appliedRange) return;
    this.rangeDays = next;
    this.applyRange(next);
  }

  private applyRange(value: string): void {
    const span = Number(value);
    if (span !== 7 && span !== 30 && span !== 90) return;
    this.appliedRange = String(span);
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (span - 1));
    this.fromDate = this.toInputDate(start);
    this.toDate = this.toInputDate(end);
    this.load();
  }

  toggleCampaigns(): void {
    this.showAllCampaigns = !this.showAllCampaigns;
    this.load();
  }

  createCampaign(): void {
    this.router.navigate(['/main/broadcast-campaigns']);
  }

  openSequences(): void {
    this.router.navigate(['/main/sequences']);
  }

  load(): void {
    const requestId = ++this.requestId;
    this.loading = true;
    this.error = '';
    const from = new Date(`${this.fromDate}T00:00:00`);
    const to = new Date(`${this.toDate}T23:59:59.999`);
    this.api.getOverview({
      client_id: this.localStorage.getClientId(),
      from: from.toISOString(),
      to: to.toISOString(),
      limit: this.showAllCampaigns ? 0 : 5,
    }).subscribe({
      next: (res: any) => {
        if (requestId !== this.requestId) return;
        this.report = res || this.emptyReport();
        this.syncAudience();
        this.loading = false;
        this.scheduleCharts();
      },
      error: () => {
        if (requestId !== this.requestId) return;
        this.report = this.emptyReport();
        this.syncAudience();
        this.error = 'Marketing analytics could not be loaded for this period.';
        this.loading = false;
        this.scheduleCharts();
      },
    });
  }

  count(metric: Metric | undefined): string {
    if (metric?.value == null) return '0';
    return Number(metric.value).toLocaleString();
  }

  rateText(metric: Metric | undefined): string {
    if (metric?.value == null) return '—';
    return `${Number(metric.value).toFixed(1)}%`;
  }

  changeText(metric: Metric | undefined): string {
    if (!metric) return '';
    if (metric.changeLabel === 'New') return 'New vs previous period';
    if (metric.changePct == null) return '';
    const value = Number(metric.changePct);
    if (value === 0) return '0% vs previous period';
    const arrow = value > 0 ? '↑' : '↓';
    return `${arrow} ${Math.abs(value).toFixed(1)}% vs previous period`;
  }

  changeTone(metric: Metric | undefined): string {
    if (!metric || metric.changeLabel === 'New') return 'up';
    if (metric.changePct == null || Number(metric.changePct) === 0) return 'flat';
    return Number(metric.changePct) > 0 ? 'up' : 'down';
  }

  channelLabel(channel: string): string {
    if (channel === 'whatsapp') return 'WhatsApp';
    if (channel === 'call') return 'Call';
    return 'Email';
  }

  cell(value: number | null | undefined): string {
    if (value == null) return '—';
    return Number(value).toLocaleString();
  }

  share(value: number): string {
    const total = this.report?.audience?.total || 0;
    if (!total) return '—';
    return `${Math.round((value / total) * 100)}%`;
  }

  money(value: number | null | undefined): string {
    if (value == null) return '—';
    return `$${Number(value).toLocaleString()}`;
  }

  openCampaign(row: any): void {
    const id = row.campaignId;
    if (row.channel === 'whatsapp') {
      this.router.navigate(['/main/whatsapp-campaign-analytics', id]);
      return;
    }
    if (row.channel === 'call') {
      this.router.navigate(['/main/call-campaign-analytics', id]);
      return;
    }
    this.router.navigate(['/main/email-campaign-anlaytics', id]);
  }

  openActivity(item: any): void {
    if (item.linkType === 'sequence') {
      this.router.navigate(['/main/sequence', item.linkId]);
      return;
    }
    this.openCampaign({ campaignId: item.linkId, channel: item.channel });
  }

  activityTone(item: any): string {
    if (item?.linkType === 'sequence') return 'green';
    if (item?.channel === 'whatsapp') return 'purple';
    if (item?.channel === 'call') return 'orange';
    return 'blue';
  }

  relativeTime(value: string): string {
    const then = new Date(value).getTime();
    if (!then) return '';
    const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  private buildAudienceSlices() {
    const audience = this.report?.audience || {};
    return [
      { label: 'New Contacts', value: audience.newContacts || 0, color: '#3b82f6' },
      { label: 'Engaged', value: audience.engaged || 0, color: '#22c55e' },
      { label: 'Unengaged', value: audience.unengaged || 0, color: '#94a3b8' },
      { label: 'Opt-outs', value: audience.optOuts || 0, color: '#ef4444' },
      { label: 'Suppressed', value: audience.suppressed || 0, color: '#1e293b' },
    ];
  }

  private syncAudience(): void {
    this.audienceSlices = this.buildAudienceSlices();
    const total = this.audienceSlices.reduce((sum, slice) => sum + slice.value, 0);
    if (!total) {
      this.donutGradient = 'conic-gradient(#e2e8f0 0% 100%)';
      return;
    }
    let cursor = 0;
    const stops = this.audienceSlices.map((slice) => {
      const start = cursor;
      cursor += (slice.value / total) * 100;
      return `${slice.color} ${start}% ${cursor}%`;
    });
    this.donutGradient = `conic-gradient(${stops.join(', ')})`;
  }

  private scheduleCharts(): void {
    clearTimeout(this.chartTimer);
    this.chartTimer = setTimeout(() => this.renderCharts());
  }

  private renderCharts(): void {
    this.renderChannelChart();
    this.renderTrendChart();
  }

  private renderChannelChart(): void {
    const canvas = this.channelCanvas?.nativeElement;
    if (!canvas) return;
    this.channelChart?.destroy();
    const channels = this.report?.channels || [];
    const value = (name: string, field: string) =>
      Number(channels.find((channel: any) => channel.channel === name)?.[field] || 0);

    this.channelChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['Email', 'WhatsApp', 'Call'],
        datasets: [
          {
            label: 'Sent',
            data: [value('email', 'sent'), value('whatsapp', 'sent'), value('call', 'sent')],
            backgroundColor: '#3b82f6',
            borderRadius: 4,
            maxBarThickness: 28,
          },
          {
            label: 'Delivered',
            data: [value('email', 'delivered'), value('whatsapp', 'delivered'), value('call', 'delivered')],
            backgroundColor: '#22c55e',
            borderRadius: 4,
            maxBarThickness: 28,
          },
          {
            label: 'Engaged',
            data: [value('email', 'engaged'), value('whatsapp', 'engaged'), value('call', 'engaged')],
            backgroundColor: '#8b5cf6',
            borderRadius: 4,
            maxBarThickness: 28,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
            labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: 'circle', color: '#64748b' },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#64748b' } },
          y: { beginAtZero: true, ticks: { color: '#94a3b8' }, grid: { color: '#f1f5f9' } },
        },
      },
    });
  }

  private renderTrendChart(): void {
    const canvas = this.trendCanvas?.nativeElement;
    if (!canvas) return;
    this.trendChart?.destroy();
    const trend = this.report?.trend || [];
    const labels = trend.map((point: any) => this.shortDate(point.date));

    this.trendChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          this.line('Sent', trend.map((point: any) => point.sent || 0), '#3b82f6'),
          this.line('Opened / Read', trend.map((point: any) => point.openedOrRead || 0), '#22c55e'),
          this.line('Clicked', trend.map((point: any) => point.clicked || 0), '#8b5cf6'),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
            labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, pointStyle: 'circle', color: '#64748b' },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#94a3b8', maxTicksLimit: 6 } },
          y: { beginAtZero: true, ticks: { color: '#94a3b8' }, grid: { color: '#f1f5f9' } },
        },
      },
    });
  }

  private line(label: string, data: number[], color: string) {
    return {
      label,
      data,
      borderColor: color,
      backgroundColor: color,
      pointRadius: 3,
      pointHoverRadius: 4,
      borderWidth: 2,
      tension: 0.35,
    };
  }

  private shortDate(value: string): string {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00Z`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }

  private toInputDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private emptyReport() {
    const metric = { value: 0, changePct: 0, changeLabel: null };
    const rateMetric = { value: null, changePct: null, changeLabel: null };
    return {
      kpis: {
        totalCampaigns: metric,
        activeCampaigns: { ...metric },
        totalAudience: { ...metric },
        messagesSent: { ...metric },
        engagementRate: rateMetric,
        deliveryRate: rateMetric,
        revenue: { value: null, changePct: null, changeLabel: null },
      },
      channels: [
        { channel: 'email', sent: 0, delivered: 0, engaged: 0 },
        { channel: 'whatsapp', sent: 0, delivered: 0, engaged: 0 },
        { channel: 'call', sent: 0, delivered: 0, engaged: 0 },
      ],
      trend: [],
      audience: {
        total: 0,
        active: 0,
        newContacts: 0,
        engaged: 0,
        unengaged: 0,
        optOuts: 0,
        suppressed: 0,
        newContactsPercent: null,
        breakdowns: { source: [], segment: [] },
      },
      topCampaigns: [],
      automation: {
        activeSequences: 0,
        contactsEnrolled: 0,
        stillActive: 0,
        goalsAchieved: null,
      },
      recentActivity: [],
    };
  }
}
